export const PHONE_COUNTRY_OPTIONS = [
  { id: "MY", code: "60", dial: "+60", label: "Malaysia" },
  { id: "SG", code: "65", dial: "+65", label: "Singapore" },
  { id: "BN", code: "673", dial: "+673", label: "Brunei" },
];

export function getPhoneCountryConfig(countryId) {
  return (
    PHONE_COUNTRY_OPTIONS.find((c) => c.id === countryId) ||
    PHONE_COUNTRY_OPTIONS[0]
  );
}

export function inferCountryFromName(countryName) {
  const name = (countryName || "").toLowerCase();
  if (name.includes("brunei")) return "BN";
  if (name.includes("singapore")) return "SG";
  if (name.includes("malaysia")) return "MY";
  return "MY";
}

/** Detect country from digits the user typed (longest country code first). */
export function detectCountryFromDigits(digits) {
  if (!digits) return null;
  if (digits.startsWith("673")) return "BN";
  if (digits.startsWith("65")) return "SG";
  if (digits.startsWith("60")) return "MY";
  if (digits.startsWith("0")) return "MY";
  return null;
}

export function stripToNationalDigits(digits, countryId, preferSelectedCountry = false) {
  if (!digits) return "";
  const cfg = getPhoneCountryConfig(countryId);

  if (preferSelectedCountry) {
    // Only strip country code when input looks like a full international number
    if (digits.startsWith(cfg.code) && digits.length > cfg.code.length + 6) {
      return digits.slice(cfg.code.length);
    }
    if (countryId === "MY" && digits.startsWith("0")) {
      return digits.slice(1);
    }
    return digits;
  }

  if (digits.startsWith(cfg.code)) {
    return digits.slice(cfg.code.length);
  }
  if (countryId === "MY" && digits.startsWith("0")) {
    return digits.slice(1);
  }
  return digits;
}

export function detectCountryFromFullNumber(digits) {
  if (!digits) return null;
  if (digits.startsWith("673") && digits.length >= 10) return "BN";
  if (digits.startsWith("65") && digits.length >= 10) return "SG";
  if (digits.startsWith("60") && digits.length >= 10) return "MY";
  return null;
}

export function normalizePhoneNumber(input, countryId = "MY", options = {}) {
  const preferSelectedCountry = options.preferSelectedCountry === true;
  const digits = (input || "").replace(/[^\d]/g, "");
  if (!digits) {
    return {
      countryId,
      national: "",
      digit_only: "",
      display: "",
    };
  }

  const detected = preferSelectedCountry
    ? detectCountryFromFullNumber(digits)
    : detectCountryFromDigits(digits);
  const resolvedCountry = detected || countryId;
  const national = stripToNationalDigits(
    digits,
    resolvedCountry,
    preferSelectedCountry,
  );
  const cfg = getPhoneCountryConfig(resolvedCountry);
  const digit_only = national ? `${cfg.code}${national}` : "";
  const display = formatPhoneDisplay(resolvedCountry, national);

  return {
    countryId: resolvedCountry,
    national,
    digit_only,
    display,
  };
}

export function formatPhoneDisplay(countryId, national) {
  if (!national) return "";
  const cfg = getPhoneCountryConfig(countryId);

  if (countryId === "MY") {
    if (national.length >= 9) {
      return `(${cfg.dial}) ${national.slice(0, 2)}-${national.slice(2)}`;
    }
    return `(${cfg.dial}) ${national}`;
  }

  if (countryId === "SG") {
    if (national.length >= 8) {
      return `(${cfg.dial}) ${national.slice(0, 4)} ${national.slice(4)}`;
    }
    return `(${cfg.dial}) ${national}`;
  }

  if (countryId === "BN") {
    if (national.length >= 7) {
      return `(${cfg.dial}) ${national.slice(0, 3)} ${national.slice(3)}`;
    }
    return `(${cfg.dial}) ${national}`;
  }

  return `(${cfg.dial}) ${national}`;
}

/** National number only — no country prefix repeated in the input field. */
export function formatNationalDisplay(countryId, national) {
  if (!national) return "";
  return national;
}

export function splitPhoneInput(input, countryId = "MY") {
  const normalized = normalizePhoneNumber(input, countryId, {
    preferSelectedCountry: true,
  });
  return {
    ...normalized,
    nationalDisplay: formatNationalDisplay(
      normalized.countryId,
      normalized.national,
    ),
  };
}

export function isValidRegionalPhone(input, countryId = "MY") {
  const { countryId: resolved, national } = normalizePhoneNumber(
    input,
    countryId,
    { preferSelectedCountry: true },
  );
  if (!national) return false;

  if (resolved === "MY") {
    return /^1\d{8,9}$/.test(national);
  }

  if (resolved === "SG") {
    // 8-digit SG numbers (mobile 8/9, some lines 3/6)
    return /^[3689]\d{7}$/.test(national);
  }

  if (resolved === "BN") {
    return /^[2789]\d{6,7}$/.test(national);
  }

  return false;
}
